import { useState } from "react";
import { type TimelineEvent } from "../lib/config";
import { type Language, getTranslation, getPageTranslations } from "../lib/translations";
import { Modal } from "./Modal";
import { Card, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Calendar, Link as LinkIcon } from "lucide-react";

interface TimelineProps {
  events: TimelineEvent[];
  lang: Language;
}

function parseDuration(durationStr: string) {
  if (!durationStr) return { start: "", end: "" };
  const parts = durationStr.split(/\s*-\s*/);
  if (parts.length === 2) {
    const start = parts[0]?.trim() || "";
    const end = parts[1]?.trim() || "";
    return { start, end };
  }
  return { start: durationStr.trim(), end: "" };
}

interface TimelineEventModalProps {
  event: TimelineEvent;
  lang: Language;
  onClose: () => void;
  closeLabel: string;
}

function TimelineEventModal({ event, lang, onClose, closeLabel }: TimelineEventModalProps) {
  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={getTranslation(event.role, lang)}
      subtitle={getTranslation(event.company, lang)}
      closeLabel={closeLabel}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-brand-secondary bg-brand-primary/5 dark:bg-brand-primary/10 p-3 rounded-lg border border-brand-primary/10">
          <Calendar className="h-4 w-4 text-brand-primary" />
          <span className="font-semibold">{getTranslation(event.duration, lang)}</span>
        </div>
        <ul className="space-y-3 list-disc pl-5">
          {getTranslation(event.details, lang).map((detail, idx) => (
            <li key={idx} className="text-foreground/90 leading-relaxed text-sm">
              {detail}
            </li>
          ))}
        </ul>
        {event.links && event.links.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border/40">
            <div className="flex flex-wrap gap-2">
              {event.links.slice(0, 5).map((link, linkIdx) => (
                <a
                  key={linkIdx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors border border-brand-primary/20 hover:border-brand-primary/40 rounded-full px-3.5 py-1.5 bg-brand-primary/5 hover:bg-brand-primary/10 shadow-sm"
                >
                  <LinkIcon className="h-3.5 w-3.5" />
                  <span>{lang === "tr" ? link.name_tr || link.name_en : link.name_en || link.name_tr}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function Timeline({ events, lang }: TimelineProps) {
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const t = getPageTranslations(lang);

  return (
    <div className="relative border-l border-border/40 pl-6 ml-4 my-8 space-y-12">
      {events.map((event) => {
        const translatedDuration = getTranslation(event.duration, lang);
        const parsedDuration = parseDuration(translatedDuration);

        return (
          <div key={event.id} className="relative">
            {/* Timeline Node Circle */}
            <span className="absolute left-[-31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-background border-2 border-brand-primary shadow-[0_0_8px_rgba(4,114,163,0.3)] transition-all duration-300 hover:scale-125" />

            {/* Timeline Card */}
            <Card
              onClick={() => setSelectedEvent(event)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedEvent(event);
                }
              }}
              tabIndex={0}
              glowing
              className="group cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-primary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <CardHeader className="p-5 md:p-6 flex flex-row items-center justify-between gap-4">
                <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
                  {event.logo_url && (
                    <div className="h-16 w-16 rounded-2xl border border-border/40 overflow-hidden bg-white dark:bg-muted/10 flex items-center justify-center p-2 shadow-sm transition-transform duration-300 group-hover:scale-105 shrink-0">
                      <img src={event.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl font-bold group-hover:text-brand-primary transition-colors text-left">
                      {getTranslation(event.role, lang)}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-brand-secondary mt-1 text-left">
                      {getTranslation(event.company, lang)}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex flex-col items-end text-right font-mono shrink-0">
                  <span className="text-sm font-bold text-brand-primary">
                    {parsedDuration.end || parsedDuration.start}
                  </span>
                  {parsedDuration.end && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {parsedDuration.start}
                    </span>
                  )}
                </div>
              </CardHeader>
            </Card>
          </div>
        );
      })}

      {/* Modal for detail view */}
      {selectedEvent && (
        <TimelineEventModal
          event={selectedEvent}
          lang={lang}
          onClose={() => setSelectedEvent(null)}
          closeLabel={t.modal.close}
        />
      )}
    </div>
  );
}

export default Timeline;
